/**
 * RetentionCard — Feb 27 2026 · Phase 166.
 *
 * Soft, dismissable in-app card shown to users whose Premium or VIP
 * subscription has lapsed. Purpose: reduce churn by making resubscribing
 * a single tap without being nagging.
 *
 * Detection is purely client-side:
 *   • On successful upgrade or on filter-drawer open, `mtk_last_seen_filter_tier`
 *     is stamped with the user's active tier.
 *   • If the current tier is lower than the last-seen tier AND the last-seen
 *     was premium/vip → the sub lapsed → we show the card.
 *
 * Dismiss behavior:
 *   • Tapping the ✕ hides the card for 7 days (stored in
 *     `mtk_retention_card_hide_until` as an ISO string).
 *   • Tapping "Resubscribe" navigates to /subscription and hides for 30 days
 *     (they're actively considering — don't nag them).
 *   • Clears both stamps on next successful resubscribe (done in
 *     Subscription.jsx via localStorage.setItem('mtk_last_seen_filter_tier',
 *     newTier); the card just naturally stops rendering).
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Sparkles, X } from 'lucide-react';

const TIER_RANK = { free: 0, premium: 1, vip: 2 };

export default function RetentionCard({ currentTier }) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  // The tier the user PREVIOUSLY held (highest seen locally). Only rendered
  // if it was premium/vip and current is now lower.
  const [lastSeen, setLastSeen] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const seen = localStorage.getItem('mtk_last_seen_filter_tier') || 'free';
      const hideUntil = localStorage.getItem('mtk_retention_card_hide_until');
      if (hideUntil) {
        const t = new Date(hideUntil).getTime();
        if (!Number.isNaN(t) && t > Date.now()) {
          setDismissed(true);
        }
      }
      setLastSeen(seen);
    } catch { /* private-mode fallback: card just won't show */ }
  }, []);

  const cur = TIER_RANK[currentTier] ?? 0;
  const prev = TIER_RANK[lastSeen] ?? 0;
  const lapsed = prev > cur && prev >= TIER_RANK.premium;   // was paid, now downgraded

  if (!lapsed || dismissed || !lastSeen) return null;

  const lostTier = lastSeen;                                 // 'premium' | 'vip'
  const tone = lostTier === 'vip' ? '#B91C1C' : '#7C3AED';
  const label = lostTier === 'vip' ? 'VIP' : 'Premium';

  const hideForDays = (days) => {
    try {
      const until = new Date(Date.now() + days * 86400000).toISOString();
      localStorage.setItem('mtk_retention_card_hide_until', until);
    } catch { /* silent */ }
    setDismissed(true);
  };

  const onResubscribe = () => {
    hideForDays(30);
    navigate('/subscription');
  };

  return (
    <div
      className="mx-4 mt-3 mb-1 rounded-2xl border shadow-sm p-3.5 flex items-start gap-3"
      style={{
        borderColor: `${tone}33`,
        background: `linear-gradient(135deg, ${tone}0A 0%, ${tone}05 100%)`,
      }}
      data-testid={`retention-card-${lostTier}`}
      role="status">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ background: `${tone}18`, color: tone }}
      >
        {lostTier === 'vip' ? <Crown size={16} /> : <Sparkles size={16} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-[#0F0F10] leading-snug">
          Your {label} perks paused today
        </p>
        <p className="text-[11.5px] text-[#8A8A93] leading-snug mt-0.5">
          Resume in one tap — your saved searches and profile are still here waiting.
        </p>
        <button
          onClick={onResubscribe}
          data-testid={`retention-card-resubscribe-${lostTier}`}
          className="mt-2.5 inline-flex items-center gap-1 text-white text-[11.5px] font-semibold tracking-tight px-3 py-1.5 rounded-full transition-transform hover:scale-[1.03] active:scale-[0.98]"
          style={{ background: tone }}
        >
          Resubscribe to {label}
        </button>
      </div>
      <button
        onClick={() => hideForDays(7)}
        aria-label="Dismiss for now"
        data-testid={`retention-card-dismiss-${lostTier}`}
        className="w-7 h-7 rounded-full hover:bg-black/[0.04] flex items-center justify-center transition-colors flex-shrink-0"
      >
        <X size={13} className="text-zinc-400" />
      </button>
    </div>
  );
}
